<template>
    <Card overlay :loading="loading">
        <template #nav>
            <div :key="spaceId" class="nav-wrap gap-3">
                <Multiselect
                    :key="timeRangeString"
                    :fetch-options-function="fetchSources"
                    :placeholder="$t('sources')"
                    :selected-options-label="$t('selectedSources')"
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="sources = $event"
                    @loading="sourcesLoading = $event"
                />
                <DropdownFilter v-model="type" :items="typeItems" :prefix="`${$t('type')}: `" :disabled="loading" />
                <DropdownFilter v-model="conversion" :items="conversionItems" :prefix="`${$t('salesSummary.conversion')}: `" :disabled="loading" />
                <DropdownFilter v-model="period" :items="periodItems" :prefix="`${$t('period')}: `" :disabled="loading" />

                <Multiselect
                    v-if="showProductGroupsResults"
                    :fetch-options-function="fetchGroupsOptions"
                    :placeholder="$t('productGroups')"
                    :selected-options-label="$t('selectedProductGroups')"
                    preselect-all
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="groups = $event"
                    @loading="groupsLoading = $event"
                />

                <div class="w-100"></div>

                <b-form-checkbox v-model="showProductGroupsResults" switch :disabled="loading">
                    {{ $t('showProductGroupsResults') }}
                </b-form-checkbox>
            </div>
        </template>

        <div class="d-flex flex-wrap gap-4">
            <div style="flex: 3 1 300px">
                <EChart
                    :options="columnChartOptions"
                    type="time"
                    :group-by="period"
                    card
                    class="shadow-none"
                    :header="$t('salesSummary2')"
                    :loading="requestAllSalesChart.getLoading() || filtersLoading"
                />
            </div>
            <div style="flex: 1 1 300px">
                <EChart
                    :options="pieChartOptions"
                    type="pie"
                    card
                    class="shadow-none"
                    :header="`${$t('summary')}: ${pieTotalFormatted}`"
                    :loading="requestAllSalesChart.getLoading() || filtersLoading"
                />
            </div>
        </div>

        <div class="dropdown-divider border-bottom border-light my-3"></div>

        <DataTable
            :fields="tableFields"
            :items="tableItems"
            :loading="requestSummary.getLoading() || filtersLoading"
            :hover="false"
            :overflow="false"
            :header="$t('salesSummary2')"
            show-export-button
            class="mb-2"
        >
            <template #cell(date)="{ item }">
                <template v-if="period === 'year'">{{ $format.date(item.date, 'yyyy') }}</template>
                <template v-else-if="period === 'quarter'">{{ $format.date(item.date, 'QQQ yyyy') }}</template>
                <template v-else-if="period === 'month'">{{ $format.date(item.date, 'LLLL yyyy') }}</template>
                <template v-else>{{ $format.date(item.date) }}</template>
            </template>
            <template #cell(views)="{ item }">{{ $format.number(item.views, 0) }}</template>
            <template #cell(clicks)="{ item }">{{ $format.number(item.clicks, 0) }}</template>
            <template #cell(ctr)="{ item }">{{ $format.percent(item.ctr) }}</template>
            <template #cell(transactions)="{ item }">{{ $format.number(item.transactions, 0) }}</template>
            <template #cell(cr)="{ item }">{{ $format.percent(item.cr) }}</template>
            <template #cell(quantity)="{ item }">{{ $format.number(item.quantity, 0) }}</template>

            <template #cell(shareQuantity)="{ item }">
                <ShareMultiple
                    :values="[safeNum(item.ownQuantity), safeNum(item.otherQuantity), safeNum(item.unknownQuantityComputed)]"
                    :labels="quantityShareLabels"
                    :colors="colors"
                    :value-formatter="$format.number"
                />
            </template>

            <template #cell(amount)="{ item }">{{ $format.currency(item.amount) }}</template>

            <template #cell(shareAmount)="{ item }">
                <ShareMultiple
                    :values="[safeNum(item.ownAmount), safeNum(item.otherAmount), safeNum(item.unknownAmountComputed)]"
                    :labels="amountShareLabels"
                    :colors="colors"
                    :value-formatter="$format.currency"
                />
            </template>
        </DataTable>
    </Card>
</template>

<script>
    import Card from '@/components/common/Card.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';
    import EChart from '@/components/common/EChart.vue';
    import DataTable from '@/components/common/DataTable.vue';
    import ShareMultiple from './ShareMultiple.vue';
    import salesReportMixin from '../salesReportMixin';
    import { SALES_SHARE_COLORS } from '../constants';
    import { fetchSources, fetchGroups } from '../utils';

    export default {
        name: 'SalesSalesSummary',
        mixins: [salesReportMixin],
        components: { Card, Multiselect, DropdownFilter, EChart, DataTable, ShareMultiple },
        data() {
            return {
                requestAllSalesChart: this.$req('reportsSales/AllSalesChart'),
                requestSummary: this.$req('reportsSales/Summary'),
                requestSources: this.$req('reportsSales/UniqueSources'),
                requestGroups: this.$req('producerProductGroups/SpaceGroups'),
                type: 'amount',
                typeItems: ['amount', 'quantity', 'orders'],
                conversion: 24,
                conversionItems: ['all', 24],
                period: 'day',
                periodItems: ['day', 'month', 'quarter', 'year'],
                sources: [],
                sourcesLoading: false,
                groups: [],
                groupsLoading: false,
                showProductGroupsResults: false,
                chartData: [],
                summaryData: [],
                colors: SALES_SHARE_COLORS,
            };
        },
        computed: {
            filtersLoading() {
                return this.sourcesLoading || this.groupsLoading;
            },
            pieTotal() {
                if (!this.chartData?.length) return 0;
                const dataKeys = Object.keys(this.chartData[0] || {}).filter(key => key !== 'date');
                return dataKeys.reduce((total, dataKey) => total + this.chartData.reduce((sum, row) => sum + Number(row[dataKey] ?? 0), 0), 0);
            },
            pieTotalFormatted() {
                const isAmount = this.type === 'amount';
                return isAmount ? this.$format.currency(this.pieTotal) : this.$format.number(this.pieTotal, 0);
            },
            loading() {
                return this.filtersLoading || this.requestAllSalesChart.getLoading() || this.requestSummary.getLoading();
            },
            apiParams() {
                const { startDate, endDate } = this.timeRange || {};
                const urlParams = { spaceId: this.spaceId };
                const requestParams = { startDate, endDate };
                if (this.sources?.length) requestParams.sources = this.sources.join(',');
                if (this.showProductGroupsResults && this.groups?.length) {
                    requestParams.groups = this.groups.join(',');
                }
                if (this.conversion !== 'all') requestParams.conversionTime = Number(this.conversion);
                requestParams.groupBy = this.period;
                requestParams.feature = this.type;
                return { urlParams, requestParams };
            },
            columnChartOptions() {
                if (!this.chartData?.length) return { series: [] };
                const dataKeys = Object.keys(this.chartData[0] || {}).filter(key => key !== 'date');
                const xAxis = [];
                const series = dataKeys.map(dataKey => ({ name: this.seriesLabel(dataKey), type: 'bar', stack: 'stack', areaStyle: null, data: [] }));
                this.chartData.forEach(row => {
                    xAxis.push(row.date);
                    dataKeys.forEach((dataKey, index) => series[index].data.push(Number(row[dataKey] ?? 0)));
                });
                const isAmount = this.type === 'amount';
                return {
                    tooltip: {
                        trigger: 'axis',
                        valueFormatter: value => (isAmount ? this.$format.currency(value) : this.$format.number(value, 0)),
                    },
                    xAxis: { type: 'category', data: xAxis },
                    yAxis: {
                        type: 'value',
                        axisLabel: { formatter: value => (isAmount ? this.$format.currency(value, null, null, 0) : this.$format.number(value, 0)) },
                    },
                    series,
                };
            },
            pieChartOptions() {
                if (!this.chartData?.length) return { series: [] };
                const dataKeys = Object.keys(this.chartData[0] || {}).filter(key => key !== 'date');
                const totals = dataKeys.map(dataKey => this.chartData.reduce((sum, row) => sum + Number(row[dataKey] ?? 0), 0));
                const isAmount = this.type === 'amount';
                return {
                    tooltip: {
                        trigger: 'item',
                        valueFormatter: value => (isAmount ? this.$format.currency(value) : this.$format.number(value, 0)),
                    },
                    series: [
                        {
                            name: this.$t('summary'),
                            type: 'pie',
                            data: dataKeys.map((dataKey, index) => ({ name: this.seriesLabel(dataKey), value: totals[index] })),
                        },
                    ],
                };
            },
            tableFields() {
                return [
                    { key: 'date', label: this.$t('salesSummary.date'), class: 'text-left', exportable: true, exportFormatter: value => this.formatDate(value) },
                    { key: 'views', label: this.$t('salesSummary.views'), class: 'text-right', exportable: true, exportFormatter: value => this.$format.number(value, 0) },
                    { key: 'clicks', label: this.$t('salesSummary.clicks'), class: 'text-right', exportable: true, exportFormatter: value => this.$format.number(value, 0) },
                    { key: 'ctr', label: this.$t('salesSummary.ctr'), class: 'text-right', exportable: true, exportFormatter: value => this.$format.percent(value) },
                    { key: 'transactions', label: this.$t('salesSummary.transaction'), class: 'text-right', exportable: true, exportFormatter: value => this.$format.number(value, 0) },
                    { key: 'cr', label: this.$t('salesSummary.cr'), class: 'text-right', exportable: true, exportFormatter: value => this.$format.percent(value) },
                    { key: 'quantity', label: this.$t('salesSummary.quantity'), class: 'text-right', exportable: true, exportFormatter: value => this.$format.number(value, 0) },
                    { key: 'shareQuantity', label: this.$t('salesTransactions.share'), class: 'text-right', exportable: false },
                    { key: 'amount', label: this.$t('salesSummary.amount'), class: 'text-right', exportable: true, exportFormatter: value => this.$format.currency(value) },
                    { key: 'shareAmount', label: this.$t('salesTransactions.share'), class: 'text-right', exportable: false },
                ];
            },
            tableItems() {
                return (this.summaryData || []).map(row => {
                    const views = this.safeNum(row.views);
                    const clicks = this.safeNum(row.clicks);
                    const transactions = this.safeNum(row.transactions);
                    const quantity = this.safeNum(row.quantity);
                    const amount = this.safeNum(row.amount);
                    const ctr = views ? (clicks / views) * 100 : 0;
                    const cr = clicks ? (transactions / clicks) * 100 : 0;
                    const ownQuantity = this.safeNum(row.ownQuantity);
                    const otherQuantity = this.safeNum(row.otherQuantity);
                    const unknownQuantityComputed = Math.max(0, quantity - ownQuantity - otherQuantity);
                    const ownAmount = this.safeNum(row.ownAmount);
                    const otherAmount = this.safeNum(row.otherAmount);
                    const unknownAmountComputed = Math.max(0, amount - ownAmount - otherAmount);
                    return {
                        ...row,
                        views,
                        clicks,
                        transactions,
                        quantity,
                        amount,
                        ctr,
                        cr,
                        ownQuantity,
                        otherQuantity,
                        unknownQuantityComputed,
                        ownAmount,
                        otherAmount,
                        unknownAmountComputed,
                    };
                });
            },
            quantityShareLabels() {
                return [
                    `${this.$t('quantity2')}: {{value}} ${this.$t('salesSummaryQuantityItem')} ({{valuePercentFormatted}}) ${this.$t('ownProducts').toLowerCase()}`,
                    `${this.$t('quantity2')}: {{value}} ${this.$t('salesSummaryQuantityItem')} ({{valuePercentFormatted}}) ${this.$t('otherProducts').toLowerCase()}`,
                    `${this.$t('quantity2')}: {{value}} ${this.$t('salesSummaryQuantityItem')} ({{valuePercentFormatted}}) ${this.$t('unknownProducts').toLowerCase()}`,
                ];
            },
            amountShareLabels() {
                return [
                    `${this.$t('salesAmount')}: {{valueFormatted}} ({{valuePercentFormatted}}) ${this.$t('ownProducts').toLowerCase()}`,
                    `${this.$t('salesAmount')}: {{valueFormatted}} ({{valuePercentFormatted}}) ${this.$t('otherProducts').toLowerCase()}`,
                    `${this.$t('salesAmount')}: {{valueFormatted}} ({{valuePercentFormatted}}) ${this.$t('unknownProducts').toLowerCase()}`,
                ];
            },
        },
        watch: {
            spaceId() {
                this.reload();
            },
            apiParams: {
                deep: true,
                handler(newValue, oldValue) {
                    if (JSON.stringify(newValue || {}) !== JSON.stringify(oldValue || {})) this.loadData();
                },
            },
        },
        methods: {
            resetFilters() {
                this.type = 'amount';
                this.conversion = 24;
                this.period = 'day';
                this.sources = [];
                this.groups = [];
                this.showProductGroupsResults = false;
                this.reload();
            },
            makeCommon(includeOnlyUrl = false) {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate } = this.timeRange || {};
                if (includeOnlyUrl) return { urlParams };
                const requestParams = {
                    startDate,
                    endDate,
                    ...(this.sources?.length ? { sources: this.sources.join(',') } : {}),
                };
                if (this.showProductGroupsResults && this.groups?.length) {
                    requestParams.groups = this.groups.join(',');
                }
                return { urlParams, requestParams };
            },
            makeChartParams() {
                const params = this.makeCommon();
                params.requestParams.groupBy = this.period;
                params.requestParams.feature = this.type;
                if (this.conversion !== 'all') params.requestParams.conversionTime = Number(this.conversion);
                return params;
            },
            makeSummaryParams() {
                const params = this.makeCommon();
                params.requestParams.groupBy = this.period;
                if (this.conversion !== 'all') params.requestParams.conversionTime = Number(this.conversion);
                return params;
            },
            async loadData() {
                if (!this.spaceId || !this.timeRange) return;
                const chartParams = this.makeChartParams();
                this.requestAllSalesChart
                    .get(chartParams)
                    .then(data => (this.chartData = Array.isArray(data) ? data : []))
                    .catch(() => (this.chartData = []));
                const summaryParams = this.makeSummaryParams();
                this.requestSummary
                    .get(summaryParams)
                    .then(data => (this.summaryData = Array.isArray(data) ? data : []))
                    .catch(() => (this.summaryData = []));
            },
            reload() {
                this.sources = [];
                [this.requestAllSalesChart, this.requestSummary].forEach(request => {
                    request?.abortAll?.();
                    request?.resetAll?.();
                });
                this.chartData = [];
                this.summaryData = [];
                this.loadData();
            },
            async fetchSources({ search }) {
                return fetchSources({ requestSources: this.requestSources, spaceId: this.spaceId, timeRange: this.timeRange, search });
            },
            fetchGroupsOptions({ search }) {
                return fetchGroups({ requestGroups: this.requestGroups, spaceId: this.spaceId, search });
            },
            formatDate(value) {
                if (this.period === 'year') return this.$format.date(value, 'yyyy');
                if (this.period === 'quarter') return this.$format.date(value, 'QQQ yyyy');
                if (this.period === 'month') return this.$format.date(value, 'LLLL yyyy');
                return this.$format.date(value);
            },
            seriesLabel(key) {
                const fallbackMap = {
                    own: this.$t('ownProducts'),
                    other: this.$t('otherProducts'),
                    others: this.$t('otherProducts'),
                    unknown: this.$t('unknownProducts'),
                    orders: this.$t('orders'),
                };
                return fallbackMap[key] || key;
            },
            safeNum(value) {
                const number = Number(value);
                return Number.isFinite(number) ? number : 0;
            },
        },
        created() {
            this.loadData();
        },
    };
</script>

<style scoped lang="scss">
    .nav-wrap {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
    }
</style>
